import React, { FC, ReactNode, useMemo, useState } from "react"
import st from './styles.module.scss'
import { HoverProvider, IconBox, RadioGroup, Switcher, SwitcherType, Type as T } from "@infobis/ui-styles-utils";
import cn from "classnames";
import { useTypedSelector } from "@hooks/useTypedSelector";
import { useActions } from "@hooks/useActions";
import KeepAlive from 'react-fiber-keep-alive';
import { TilesByGroup } from "../../../../services/map/Tiles";
import { array, set } from "fp-ts";
import { WeatherElements } from "@pages/monitoring/map/hooks/useWeatherLayers";
import { string } from "fp-ts/es6";
import colors from '@infobis/ui-styles-utils/colors'

// TODO! Decompose to single file components
export const MapTabToggles = [
    {
        id: 'rosreestr',
        title: 'Информация Росреестра'
    },
    {
        id: 'show_notes',
        title: 'Отображать заметки на карте'
    }
] as const
export type MapTabToggleKey = typeof MapTabToggles[number]['id']
export type GeoZoneTabToggle = typeof GeoZonesTabToggles[number]
const GeoZonesTabToggles = [
    {
        id: 'simplification',
        title: 'Упрощение контуров для ускорения работы'
    },
    {
        id: 'geozones_area',
        title: 'Площади полей'
    },
    {
        id: 'track_over_geozone',
        title: 'Название поля поверх трека'
    },
    {
        id: 'parent_name_for_sowing',
        title: 'Имя родительского поля для посева'
    },
    {
        id: 'layer_transparency',
        title: 'Прозрачность заливки слоев'
    }
] as const

const Tools = [
    { id: "ruler", name: "Линейка", icon: T.IconName.Ruler },
    { id: "measure", name: "Измерение площадей", icon: T.IconName.Polygon },
    { id: "print", name: "Печать карты", icon: T.IconName.Printer }
]

interface TabProp {
}

interface ToolProps {
    isActive: boolean
    icon: T.IconName
    onClick?: () => any
}

enum ToolPanel { Layers = "layers", Settings = "settings" }

interface CheckboxProps extends React.InputHTMLAttributes<HTMLInputElement> {
    checked?: boolean
    label?: ReactNode
}

const Checkbox: FC<CheckboxProps> = (props) => {
    const { checked = false, label, name, value, ...rest } = props
    return <label className={st.checkbox}>{label}
        <input type="checkbox" checked={checked} name={name} value={value} {...rest}/>
        <span className={st.checkbox__checkmark}>
        </span>
    </label>
}

type RadioGroupVariant = (string | { id: string, label: string })

const Tool: FC<ToolProps> = (props) => {
    const {isActive} = props;
    return <HoverProvider className={cn(st.map_tool, isActive && st['map_tool--active'])} onClick={props.onClick}>
        <IconBox
            icon={props.icon}
            size={T.Size.LARGE}
            color={isActive ? colors.MAP_CONTENT_HOVER : ''}
            hoverColor={colors.MAP_CONTENT_HOVER}
        />
    </HoverProvider>
}

export const ToggleElement: FC<{ title: string } & SwitcherType> = (props) => {
    const { title, ...rest } = props
    return <div className={st.toggle_element}>
        <div className={st.toggle_element__title}>{title}</div>
        <Switcher {...rest}/>
    </div>
}

interface ParameterBlockProps {
    title: string
    children: ReactNode
    id: string
    switchable?: boolean,
    enabled?: boolean
    onChange?: (key: string, enabled: boolean) => any
    bodyCls?: string,
}

const LayerBlock: FC<ParameterBlockProps> = (props) => {
    const [isOpen, setOpen] = useState(false)
    const { switchable = true, enabled = false, onChange, bodyCls } = props
    return <div className={st.parameters}>
        <div className={cn(st.parameters__header, isOpen && st['parameters__header--opened'])}>
            <div className={st.parameters__header_title} onClick={() => setOpen(!isOpen)}>
                {props.title}
                <IconBox
                    icon={isOpen ? T.IconName.ChevronDown : T.IconName.ChevronRight}
                    size={T.Size.SMALL}
                    sizePadding={T.Size.EXTRA_SMALL}
                />
            </div>
            {switchable && (
                <div className={st.parameters__header_switch}>
                    <Switcher
                        size={T.Size.SMALL}
                        checked={enabled}
                        onChange={(e) => {
                            onChange && onChange(e.target.value, e.target.checked)
                        }}
                        value={props.id}
                    />
                </div>
            )}
        </div>
        {<div className={cn(st.parameters__body, {
            [st.parameters__body_disabled]: !enabled,
            [st.parameters__body_visible]: isOpen
        }, bodyCls)}>{props.children}</div>}
    </div>
}

enum LBlock {
    MAP_VIEW = "map_view",
    MARKERS = "markers",
    WEATHER = "weather",
    WORKS = "works",
    AGROCHEMISTRY = "agrochemistry",
    TRANSPARENCY = "transparency"
}

interface WorkRowProps extends CheckboxProps {
    color?: string
    title?: string
}

const WorkRow: FC<WorkRowProps> = (props) => {
    const { color, title, ...rest } = props
    const label = <div className={st.work_row}>{color &&
        <div className={st.work_row__point} style={{ background: color }}/>}
        <div className={st.work_row__title}>{title}</div>
    </div>
    return <Checkbox
        label={label}
        {...rest}/>
}

const MarkersTab: FC<TabProp> = () => {
    const { agrochemistry } = useTypedSelector((state) => state.markersTabSettings)
    const { setMarkersTabCurrentChemistry, setMarkersTabLayerAgrochemistryEnabled } = useActions()
    const renderRadioGroup = (variants: string[], title: string | undefined = undefined) =>
        <RadioGroup variants={variants}
                    title={title}
                    name={LBlock.MARKERS}
                    checked={agrochemistry.current}
                    onChange={provider => setMarkersTabCurrentChemistry({ provider })}/>

    return <div className={cn(st.tab, st.markers_tab)}>
        <LayerBlock id={LBlock.AGROCHEMISTRY}
                    title="Агрохимия"
                    enabled={agrochemistry.enabled}
                    onChange={(k, enabled) => setMarkersTabLayerAgrochemistryEnabled({ enabled })}>
            {renderRadioGroup([
                'Гумус',
                'Азот нитратный',
                'Азот аммонийный',
                'Минеральный азот',
                'Азот легкогидрируемый',
                'Фосфор Р2О5 (по Кирасанову)',
                'Калий К2О (по Кирасанову)'
            ])}
        </LayerBlock>
    </div>
}
export type CadastreFillType = ['Заливка', 'Границы участка']
const GeoZonesTab: FC<TabProp> = () => {
    const { works, toggles } = useTypedSelector((state) => state.geoZoneTabSettings)
    const {
        setGeoZonesTabToggleState,
        setGeoZonesTabWork,
        setGeoZonesTabWorks,
        setGeoZonesTabWorksLayerEnabled
    } = useActions()

    const renderRadioGroup = (variants: CadastreFillType, title: string) =>
        <RadioGroup variants={variants}
                    title={title}
                    checked={String(toggles.cadastre)}
                    onChange={provider => setGeoZonesTabToggleState({ id: 'cadastre', enabled: provider })}/>

    const CheckAllRow = useMemo(() => {
        const id = 'check_all'
        const isChecked = set.getEq(string.Eq).equals(
            set.fromArray(string.Eq)(works.layers.map((i) => i.id)),
            set.fromArray(string.Eq)(works.current)
        ) && array.isNonEmpty(works.current)
        return <WorkRow
            key={id}
            checked={isChecked}
            value={id}
            title={'Выбрать все'}
            onChange={(e) => {
                const ids = e.target.checked ? works.layers.map((i) => i.id) : []
                setGeoZonesTabWorks({ ids })
            }}/>
    }, [works.current, works.layers])

    return <div className={cn(st.tab, st.geozones_tab)}>
        <LayerBlock title="Работы" id={LBlock.WORKS}
                    enabled={works.enabled} onChange={(k, enabled) => setGeoZonesTabWorksLayerEnabled({ enabled })}
        >
            {array.isEmpty(works.layers) ? 'Нет данных...' : [CheckAllRow].concat(works.layers.map((w) =>
                <WorkRow
                    key={w.id}
                    color={w.color}
                    name={"work_" + w.id}
                    checked={works.current.indexOf(String(w.id)) !== -1}
                    value={w.id}
                    title={w.title}
                    onChange={(e) => {
                        setGeoZonesTabWork({ id: e.target.value })
                    }}/>))}
        </LayerBlock>
        {GeoZonesTabToggles.map(c =>
            <ToggleElement
                size={T.Size.SMALL}
                checked={Boolean(toggles[c.id])}
                value={c.id}
                title={c.title} onChange={(e) => setGeoZonesTabToggleState({
                id: e.target.value,
                enabled: e.target.checked
            })}/>
        )}
        <hr className={st.hr}/>
        {renderRadioGroup(['Заливка', 'Границы участка'], 'Цвет кадастровых полей')}
    </div>
}
const MapTab: FC<TabProp> = (props) => {
    const { mapView, mapWeather, toggles } = useTypedSelector((state) => state.mapTabSettings)

    const {
        setMapTabLayerProvider,
        setMapTabWeatherEnabled,
        // setMapTabMapViewEnabled,
        setMapTabToggleState,
        setMapTabWeather,
        setMapTabWeatherLayers
    } = useActions()

    const CheckAllRow = useMemo(() => {
            const id = 'check_all'
            const isChecked = set.getEq(string.Eq).equals(
                set.fromArray(string.Eq)(WeatherElements.map((i) => i.id)),
                set.fromArray(string.Eq)(mapWeather.current)
            )
            return <WorkRow
                key={id}
                checked={isChecked}
                value={id}
                title={'Выбрать все'}
                onChange={(e) => {
                    const ids = e.target.checked ? WeatherElements.map((i) => i.id) : []
                    setMapTabWeatherLayers({ ids })
                }}/>
        }
        , [mapWeather.current])

    const renderRadioGroup = (variants: RadioGroupVariant[], title: string) => {
        return <RadioGroup

            variants={variants}
            title={title}
            name={LBlock.MAP_VIEW}
            checked={mapView.currentTileProvider}
            onChange={provider => setMapTabLayerProvider({ provider })}
        />
    }
    return <div className={cn(st.tab, st.map_tab)}>
        <LayerBlock
            id={LBlock.MAP_VIEW}
            title="Вид карты"
            switchable={false}
            enabled={mapView.enabled}
            bodyCls={st['parameters__body--large-space']}
            // onChange={(k, enabled) => setMapTabMapViewEnabled({ enabled })}
        >
            {TilesByGroup.map(([groupName, configurations]) =>
                renderRadioGroup(configurations.map(c => ({ id: c.id, label: c.name })), groupName)
            )}
        </LayerBlock>
        <LayerBlock title="Карта погоды" id={LBlock.WEATHER}
                    enabled={mapWeather.enabled} onChange={(k, enabled) => setMapTabWeatherEnabled({ enabled })}>
            {[CheckAllRow].concat(WeatherElements.map((w) =>
                <Checkbox
                    name={w.id}
                    value={w.id}
                    label={w.title}
                    checked={mapWeather.current.indexOf(w.id) !== -1}
                    onChange={(e) => setMapTabWeather({ id: e.target.value })}/>))}
        </LayerBlock>
        {MapTabToggles.map(c =>
            <ToggleElement
                size={T.Size.SMALL}
                value={c.id}
                checked={toggles[c.id]}
                title={c.title} onChange={(e) => setMapTabToggleState({
                    id: e.target.value as MapTabToggleKey,
                    enabled: e.target.checked
                })}
            />
        )}
    </div>
}

const Tabs = [
    {
        id: "map",
        title: "Карты"
    },
    {
        id: "geoZones",
        title: "Геозоны",
    },
    {
        id: "markers",
        title: "Маркеры"
    }
]
const LayersPanel: FC<{ onClose: () => void }> = ({ onClose }) => {
    const [activeTab, setActiveTab] = useState("map");
    const {
        resetMarkersTabTabState,
        resetMapTabState,
        resetGeoZoneTabState
    } = useActions()

    const resetHandler = () => {
        if (activeTab === "map") resetMapTabState()
        if (activeTab === "geoZones") resetGeoZoneTabState()
        if (activeTab === "markers") resetMarkersTabTabState()
    }
    // @ts-ignore
    return <div className={st.layers_container}>
        <header className={st.layers_header}>
            <div className={st.layers_header__title}>Настройки слоев</div>
            <IconBox
                icon={T.IconName.Cancel}
                color={colors.CONTROL_DEFAULT_ICON_ICON}
                size={T.Size.MEDIUM}
                sizePadding={T.Size.EXTRA_SMALL}
                onClick={onClose}
                className={st.layers_header__close}
            />
        </header>
        <section className={st.layers_body}>
            <div className={st.layers_buttons}>
                {Tabs.map((tab) => <button
                    onClick={() => setActiveTab(tab.id)}
                    className={cn(st.layers_buttons__button, tab.id === activeTab && st.layers_buttons__button_active)}>{tab.title}</button>)}
            </div>
            <div className={st.layers_content}>
                <div className={st.layers_content__wrapper}>
                    {activeTab === "map" && <KeepAlive name={activeTab}><MapTab/></KeepAlive>}
                    {activeTab === "geoZones" && <KeepAlive name={activeTab}><GeoZonesTab/></KeepAlive>}
                    {activeTab === "markers" && <KeepAlive name={activeTab}><MarkersTab/></KeepAlive>}
                </div>
            </div>
        </section>
        <footer className={st.layers_footer}>
            <button className={st.layers_button} onClick={resetHandler}>Сбросить настройки</button>
        </footer>
    </div>
}
const SettingsPanel: FC<any> = (props) => {
    return <div className={st.settings_container}>{Tools.map((tool) =>

        <HoverProvider className={cn(st.settings__tool, st['settings__tool_' + tool.id])} key={tool.id}>
                <IconBox
                    className={st.settings__tool_icon}
                    icon={tool.icon}
                    size={T.Size.LARGE}
                    color={''}
                    hoverColor={colors.MAP_CONTENT_HOVER}
                />
                <div className={st.settings__tool_title}>{tool.name}</div>
        </HoverProvider>
    )}</div>
}
const MapTools: FC<any> = (props) => {
    const [active, setToolPanel] = useState<ToolPanel | undefined>(undefined)
    return <div className={st.component_container}>
        <div className={st.map_tools}>
            <Tool isActive={active == ToolPanel.Layers} icon={T.IconName.Layers} onClick={() => {
                setToolPanel(active == ToolPanel.Layers ? undefined : ToolPanel.Layers)
            }}/>
            <Tool isActive={active == ToolPanel.Settings} icon={T.IconName.KebabHorizontal} onClick={() => {
                setToolPanel(active == ToolPanel.Settings ? undefined : ToolPanel.Settings)
            }}/>
        </div>
        {active == ToolPanel.Layers && <LayersPanel onClose={() => setToolPanel(undefined)}/>}
        {active == ToolPanel.Settings && <SettingsPanel/>}
    </div>
}

export default MapTools;
